import { useCallback, useEffect, useState } from 'react';
import type { ConversationDetail, ConversationSummary, VisitorMessage } from '@stonelink/shared';
import { api } from '../api';
import { Badge, Button, EmptyState, PageHeader, formatDate } from '../ui';

/** Widget transcripts plus the inbox of messages visitors left. */
export function ConversationsPage() {
  const [tab, setTab] = useState<'transcripts' | 'inbox'>('transcripts');

  return (
    <div>
      <PageHeader
        title="Conversations"
        description="What visitors actually asked, and any contact details they left behind."
      />

      <div className="mb-4 flex gap-1 rounded-xl border border-line bg-white/5 p-1">
        {(
          [
            ['transcripts', 'Transcripts'],
            ['inbox', 'Message inbox'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setTab(value)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
              tab === value ? 'bg-brand text-[#15102a]' : 'text-muted'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'transcripts' ? <Transcripts /> : <Inbox />}
    </div>
  );
}

function Transcripts() {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [selected, setSelected] = useState<ConversationDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        setConversations(await api.listConversations('widget'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const open = async (id: string) => {
    setSelected(await api.getConversation(id));
  };

  if (loading) return <p className="text-sm text-muted">Loading…</p>;
  if (conversations.length === 0) {
    return (
      <EmptyState
        title="No conversations yet"
        hint="Once the widget is live on your site, every chat shows up here."
      />
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,360px)_1fr]">
      <ul className="space-y-2">
        {conversations.map((conversation) => (
          <li key={conversation.id}>
            <button
              type="button"
              onClick={() => void open(conversation.id)}
              className={`w-full rounded-xl border p-3 text-left transition ${
                selected?.conversation.id === conversation.id
                  ? 'border-brand bg-brand/10'
                  : 'border-line bg-glass-strong hover:border-line-strong'
              }`}
            >
              <p className="truncate text-sm font-medium">
                {conversation.preview ?? 'Empty conversation'}
              </p>
              <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-muted">
                <span>{formatDate(conversation.createdAt)}</span>
                <span>· {conversation.messageCount} messages</span>
                {conversation.endUserLabel && (
                  <Badge tone="good">{conversation.endUserLabel}</Badge>
                )}
                {conversation.visitorMessageCount > 0 && <Badge tone="brand">Left a message</Badge>}
              </div>
            </button>
          </li>
        ))}
      </ul>

      <div className="glass p-5">
        {!selected ? (
          <p className="text-sm text-muted">Pick a conversation to read the transcript.</p>
        ) : (
          <div className="space-y-3">
            {selected.messages.map((message) => (
              <div
                key={message.id}
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  message.role === 'user'
                    ? 'ml-auto rounded-br-md bg-brand text-[#15102a]'
                    : 'mr-auto rounded-bl-md border border-line bg-glass-strong'
                }`}
              >
                {message.content}
              </div>
            ))}
            {selected.visitorMessages.length > 0 && (
              <div className="mt-4 rounded-xl border border-brand/30 bg-brand/10 p-3 text-sm">
                <p className="font-medium text-brand">Contact details left in this chat</p>
                {selected.visitorMessages.map((message) => (
                  <p key={message.id} className="mt-1 text-soft">
                    {message.contact} — {message.body}
                  </p>
                ))}
              </div>
            )}
            {selected.accountLookups.length > 0 && (
              // What the assistant read from the business's own backend during
              // this chat, so the owner can audit it alongside the transcript.
              <div className="mt-4 rounded-xl border border-line bg-white/5 p-3 text-sm">
                <p className="font-medium">Account lookups in this chat</p>
                {selected.accountLookups.map((lookup) => (
                  <p key={lookup.id} className="mt-1 flex items-center gap-2 text-muted">
                    <span className="font-mono text-xs">{lookup.action}</span>
                    <Badge tone={lookup.status === 'ok' ? 'good' : 'warn'}>{lookup.status}</Badge>
                    <span className="text-xs">{lookup.durationMs} ms</span>
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Inbox() {
  const [messages, setMessages] = useState<VisitorMessage[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setMessages(await api.listVisitorMessages());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <p className="text-sm text-muted">Loading…</p>;
  if (messages.length === 0) {
    return (
      <EmptyState
        title="Inbox is empty"
        hint="When the assistant cannot help and a visitor leaves their details, the message lands here."
      />
    );
  }

  return (
    <ul className="space-y-2">
      {messages.map((message) => (
        <li
          key={message.id}
          className="glass-flat flex flex-wrap items-start justify-between gap-3 p-4"
        >
          <div className="min-w-0">
            <p className="text-sm font-medium">{message.contact}</p>
            <p className="mt-1 text-sm text-muted">{message.body}</p>
            <p className="mt-1.5 text-xs text-muted">{formatDate(message.createdAt)}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge tone={message.status === 'new' ? 'warn' : 'good'}>{message.status}</Badge>
            {message.status === 'new' && (
              <Button
                variant="secondary"
                onClick={async () => {
                  await api.setVisitorMessageStatus(message.id, 'handled');
                  await load();
                }}
              >
                Mark handled
              </Button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
